import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useServiceHealthStore, ServiceHealthOverview } from '@/stores/serviceHealthStore';
import { useServicesStore } from '@/stores/servicesStore';
import { useEnvironmentsStore } from '@/stores/environmentsStore';
import { useUserTeamsStore } from '@/stores/userTeamsStore';
import { ServiceHealthStatus } from '@/types/api';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger, Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Checkbox, Input, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Tabs, TabsContent, TabsList, TabsTrigger } from '@nimbitlabs/ui';
import { 
  Search, 
  Activity, 
  CheckCircle, 
  AlertTriangle, 
  XCircle, 
  HelpCircle,
  Loader2,
  RefreshCw,
  ExternalLink,
  Users,
  Package
} from 'lucide-react';

export const ServiceHealthDashboard = () => {
  const { healthData, loading, error, getOverallHealth, triggerHealthCheck } = useServiceHealthStore();
  const { services } = useServicesStore();
  const { environments } = useEnvironmentsStore();
  const { userTeamIds } = useUserTeamsStore();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [serviceFilter, setServiceFilter] = useState<string>('all');
  const [environmentFilter, setEnvironmentFilter] = useState<string>('all');
  const [teamFilter, setTeamFilter] = useState<string>('all');
  const [showMyTeamsOnly, setShowMyTeamsOnly] = useState(false);

  // Sort environments by tier (dev -> uat -> prod) - safely handle empty array
  const sortedEnvironments = React.useMemo(() => {
    if (!environments || environments.length === 0) return [];
    return [...environments].sort((a, b) => {
    const tierOrder = ['development', 'dev', 'staging', 'uat', 'qa', 'test', 'production', 'prod'];
    const getTier = (name: string) => {
      const lowerName = name.toLowerCase();
      const index = tierOrder.findIndex(tier => lowerName.includes(tier));
      return index === -1 ? 999 : index;
    };
    return getTier(a.name) - getTier(b.name);
    });
  }, [environments]);

  // Set selected tab to first environment or empty string
  const [selectedEnvironmentTab, setSelectedEnvironmentTab] = useState<string>('');

  // Update selected tab when environments load
  useEffect(() => {
    if (sortedEnvironments.length > 0 && !selectedEnvironmentTab) {
      setSelectedEnvironmentTab(sortedEnvironments[0].id);
    }
  }, [sortedEnvironments, selectedEnvironmentTab])

  const currentAccountId = useAccountStore(state => state.currentAccount?.id);

  React.useEffect(() => {
    if (currentAccountId) {
      fetchServices();
      fetchEnvironments();
    }
  }, [currentAccountId, fetchServices, fetchEnvironments]);

  const getStatusIcon = (status: ServiceHealthStatus) => {
    switch (status) {
      case ServiceHealthStatus.Healthy:
        return <CheckCircle className="h-4 w-4" />;
      case ServiceHealthStatus.Unhealthy:
      case ServiceHealthStatus.Timeout:
      case ServiceHealthStatus.DnsFailure:
        return <XCircle className="h-4 w-4" />;
      case ServiceHealthStatus.Degraded:
        return <AlertTriangle className="h-4 w-4" />;
      case ServiceHealthStatus.Unknown:
        return <HelpCircle className="h-4 w-4" />;
      default:
        return null;
    }
  };

    const getStatusColor = (status: ServiceHealthStatus) => {
      switch (status) {
        case ServiceHealthStatus.Healthy:
          return 'bg-green-500/20 text-green-600 border-green-500/40 dark:bg-green-500/20 dark:text-green-400 dark:border-green-500/40';
        case ServiceHealthStatus.Unhealthy:
        case ServiceHealthStatus.Timeout:
        case ServiceHealthStatus.DnsFailure:
          return 'bg-red-500/15 text-red-600 border-red-500/30 dark:text-red-400';
        case ServiceHealthStatus.Degraded:
          return 'bg-yellow-500/20 text-yellow-600 border-yellow-500/40 dark:bg-yellow-500/20 dark:text-yellow-400 dark:border-yellow-500/40';
        case ServiceHealthStatus.Unknown:
          return 'bg-orange-500/15 text-orange-600 border-orange-500/30 dark:text-orange-400';
        default:
          return 'bg-orange-500/15 text-orange-600 border-orange-500/30 dark:text-orange-400';
      }
    };

  // Get unique service IDs from health data
  const servicesWithHealthChecks = new Set(healthData.map(h => h.serviceId));
  const totalServices = services.length;
  const servicesWithHealth = services.filter(s => servicesWithHealthChecks.has(s.id)).length;

  const filteredHealthData = healthData.filter(health => {
    const service = services.find(s => s.id === health.serviceId);
    const matchesTeamFilter = !showMyTeamsOnly || (service && userTeamIds.includes(service.teamId));
    const matchesSearch = (health.serviceName?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
                         (health.environmentName?.toLowerCase() || '').includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || health.status === statusFilter;
    const matchesService = serviceFilter === 'all' || health.serviceId === serviceFilter;
    const matchesEnvironment = environmentFilter === 'all' || health.environmentId === environmentFilter;
    
    return matchesTeamFilter && matchesSearch && matchesStatus && matchesService && matchesEnvironment;
  });

  const overallHealth = getOverallHealth();

  // Group health data by team, then by service
  const healthByTeam = filteredHealthData.reduce((teamAcc, health) => {
    const service = services.find(s => s.id === health.serviceId);
    const teamId = service?.teamId || 'unknown';
    const teamName = service?.team || 'Unknown Team';
    
    if (!teamAcc[teamId]) {
      teamAcc[teamId] = {
        teamName,
        services: {}
      };
    }
    
    if (!teamAcc[teamId].services[health.serviceId]) {
      teamAcc[teamId].services[health.serviceId] = {
        serviceName: health.serviceName,
        healthData: []
      };
    }
    
    teamAcc[teamId].services[health.serviceId].healthData.push(health);
    return teamAcc;
  }, {} as Record<string, { 
    teamName: string; 
    services: Record<string, { serviceName: string; healthData: ServiceHealthOverview[] }> 
  }>);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <XCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">Failed to load health data</h3>
          <p className="text-muted-foreground mb-4">{error}</p>
          <Button onClick={() => window.location.reload()}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">Service Health</h1>
          <p className="text-sm sm:text-base text-muted-foreground">
            Monitor service health across all environments
          </p>
          {!loading && (
            <p className="text-sm text-muted-foreground mt-1">
              Showing {servicesWithHealth} of {totalServices} services with health checks configured
            </p>
          )}
        </div>
        <Button onClick={() => {
          window.location.reload();
        }} className="animate-fade-in">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh All
        </Button>
      </div>

      {/* Health Overview */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <div className="h-4 bg-muted rounded w-32" />
                <div className="h-4 w-4 bg-muted rounded-full" />
              </CardHeader>
              <CardContent>
                <div className="h-8 bg-muted rounded w-16 mb-2" />
                <div className="h-3 bg-muted rounded w-24" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Healthy Services</CardTitle>
              <CheckCircle className="h-4 w-4 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{overallHealth.healthy}</div>
              <p className="text-xs text-muted-foreground">
                {overallHealth.healthyPercentage}% of all services
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Unhealthy</CardTitle>
              <XCircle className="h-4 w-4 text-red-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">{overallHealth.unhealthy}</div>
              <p className="text-xs text-muted-foreground">
                {overallHealth.unhealthyPercentage}% of all services
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Unknown</CardTitle>
              <HelpCircle className="h-4 w-4 text-gray-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-600">{overallHealth.unknown}</div>
              <p className="text-xs text-muted-foreground">
                {overallHealth.unknownPercentage}% of all services
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="my-teams-only-health" 
                checked={showMyTeamsOnly}
                onCheckedChange={(checked) => setShowMyTeamsOnly(checked === true)}
              />
              <label 
                htmlFor="my-teams-only-health" 
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Show only services from my teams
              </label>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder="Search services..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="Healthy">Healthy</SelectItem>
                <SelectItem value="Unhealthy">Unhealthy</SelectItem>
                <SelectItem value="Degraded">Degraded</SelectItem>
                <SelectItem value="Unknown">Unknown</SelectItem>
              </SelectContent>
            </Select>

            <Select value={serviceFilter} onValueChange={setServiceFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Filter by service" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Services</SelectItem>
                {services.map((service) => (
                  <SelectItem key={service.id} value={service.id}>
                    {service.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={environmentFilter} onValueChange={setEnvironmentFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Filter by environment" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Environments</SelectItem>
                {environments.map((environment) => (
                  <SelectItem key={environment.id} value={environment.id}>
                    {environment.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Environment Tabs - Only show when environments are loaded */}
      {sortedEnvironments.length > 0 && (
        <Tabs value={selectedEnvironmentTab} onValueChange={setSelectedEnvironmentTab} className="space-y-6">
          <div className="w-full overflow-x-auto py-3 px-1 sm:px-0">
            <div className="inline-flex w-max min-w-full md:min-w-0 rounded-xl bg-card shadow-soft p-[4px]">
              <TabsList className="inline-flex h-12 items-center justify-start gap-2 rounded-xl bg-gradient-to-r from-muted/40 via-muted/30 to-muted/40 p-1.5 backdrop-blur-sm w-full">
                {sortedEnvironments.map((env) => (
                <TabsTrigger
                key={env.id}
                value={env.id}
                className="relative overflow-hidden data-[state=active]:text-primary data-[state=active]:scale-[1.08] data-[state=inactive]:text-muted-foreground data-[state=inactive]:hover:bg-muted/40 data-[state=inactive]:hover:text-foreground data-[state=inactive]:hover:scale-[1.02] px-6 py-2.5 rounded-lg font-bold text-sm transition-all duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)] before:absolute before:inset-0 before:bg-primary/10 before:rounded-lg before:scale-75 before:opacity-0 before:rotate-[-2deg] data-[state=active]:before:scale-100 data-[state=active]:before:opacity-100 data-[state=active]:before:rotate-0 before:transition-all before:duration-500 before:ease-[cubic-bezier(0.34,1.56,0.64,1)] after:absolute after:inset-0 after:shadow-[0_0_20px_rgba(var(--primary-rgb),0.3)] after:rounded-lg after:scale-75 after:opacity-0 data-[state=active]:after:scale-100 data-[state=active]:after:opacity-100 after:transition-all after:duration-500 after:ease-[cubic-bezier(0.34,1.56,0.64,1)]"
            >
              <span className="relative z-10">{env.name}</span>
            </TabsTrigger>
            ))}
          </TabsList>
          </div>
        </div>

        {sortedEnvironments.map((env) => (
          <TabsContent key={env.id} value={env.id} className="space-y-4">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : (
            <Accordion type="multiple" className="space-y-4">
              {Object.entries(healthByTeam).map(([teamId, { teamName, services }]) => {
                const totalHealthChecks = Object.values(services).reduce((acc, service) => acc + service.healthData.length, 0);
                
                return (
                  <AccordionItem key={teamId} value={teamId} className="rounded-lg bg-card shadow-soft">
                    <AccordionTrigger className="px-6 py-4 hover:no-underline">
                      <div className="flex items-center justify-between w-full">
                        <div className="flex items-center gap-3">
                          <Users className="h-5 w-5 text-primary" />
                          <div className="text-left">
                            <h3 className="text-lg font-semibold">{teamName}</h3>
                            <p className="text-sm text-muted-foreground">
                              {Object.keys(services).length} service{Object.keys(services).length !== 1 ? 's' : ''} • {totalHealthChecks} health check{totalHealthChecks !== 1 ? 's' : ''}
                            </p>
                          </div>
                        </div>
                      </div>
                    </AccordionTrigger>
                    
                    <AccordionContent className="px-6 pb-4">
                      <div className="space-y-4">
                        <div className="border border-muted rounded-lg bg-muted/30">
                          <div className="px-6 py-4">
                            <div className="space-y-3">
                              {/* Service rows with health status for selected environment */}
                              {Object.entries(services).map(([serviceId, { serviceName, healthData }], index) => {
                                const envHealth = healthData.find(h => h.environmentId === env.id);
                                
                                return (
                                  <div key={serviceId} className={`flex items-center gap-4 py-3 rounded-lg hover:bg-muted/50 transition-colors ${index > 0 ? 'border-t border-border/50' : ''}`}>
                                    {/* Service name column */}
                                    <div className="flex items-center gap-3 bg-muted/50 rounded-lg px-4 py-2.5 flex-1">
                                      <Package className="h-4 w-4 text-primary flex-shrink-0" />
                                      <span className="font-semibold text-sm flex-1 text-foreground">{serviceName}</span>
                                      <Button variant="ghost" size="sm" asChild className="h-7 w-7 p-0 hover:bg-primary/10">
                                        <Link to={`/services/${serviceId}`}>
                                          <ExternalLink className="h-3.5 w-3.5" />
                                        </Link>
                                      </Button>
                                    </div>
                                    
                                    {/* Health status */}
                                    <div className="flex items-center gap-3 min-w-[200px]">
                                      {envHealth ? (
                                        <>
                                          <Badge 
                                            variant="outline"
                                            className={`text-xs ${getStatusColor(envHealth.status)}`}
                                          >
                                            {envHealth.status}
                                          </Badge>
                                          <div className="flex items-center">
                                            {getStatusIcon(envHealth.status)}
                                          </div>
                                          <Button 
                                            variant="ghost" 
                                            size="sm"
                                            className="h-7 text-xs"
                                            onClick={() => triggerHealthCheck(envHealth.serviceId, envHealth.environmentId)}
                                          >
                                            <Activity className="h-3 w-3 mr-1" />
                                            Check
                                          </Button>
                                        </>
                                      ) : (
                                        <>
                                          <Badge variant="outline" className="text-xs text-muted-foreground border-dashed">
                                            No data
                                          </Badge>
                                          <div className="h-4 w-4 rounded-full border-2 border-dashed border-muted-foreground/30"></div>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
            )}
          </TabsContent>
        ))}
        </Tabs>
      )}

      {!loading && filteredHealthData.length === 0 && (
        <Card>
          <CardContent className="text-center py-12">
            <div className="text-muted-foreground">
              <Activity className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium mb-2">No health data found</p>
              <p>Try adjusting your filters or configure health checks for your services.</p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
